import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { newIdempotencyKey } from './api';

export function useMediaQuery(query: string): boolean {
  const get = () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query).matches : false);
  const [matches, setMatches] = useState(get);
  useEffect(() => {
    const mq = window.matchMedia?.(query);
    if (!mq) return;
    const on = () => setMatches(mq.matches);
    on();
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [query]);
  return matches;
}

export const useIsMobile = () => useMediaQuery('(max-width: 767px)');

export function useDebouncedValue<T>(value: T, delay = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

/**
 * A stable Idempotency-Key for one logical operation (e.g. one open dialog). Retries of the
 * same submission reuse the key; call `reset()` after success to start a new operation.
 */
export function useIdempotencyKey(prefix?: string) {
  const ref = useRef<string>(newIdempotencyKey(prefix));
  const reset = useCallback(() => {
    ref.current = newIdempotencyKey(prefix);
  }, [prefix]);
  return { get key() { return ref.current; }, reset };
}

/** Reads/writes typed filter state to URL search params (shareable, back-button friendly). */
export function useUrlFilters<K extends string>(keys: readonly K[], multi: readonly K[] = []) {
  const [params, setParams] = useSearchParams();
  const values = useMemo(() => {
    const out = {} as Record<K, string | string[] | undefined>;
    for (const k of keys) {
      if (multi.includes(k)) {
        const all = params.getAll(k);
        out[k] = all.length ? all : undefined;
      } else out[k] = params.get(k) ?? undefined;
    }
    return out;
  }, [params, keys, multi]);

  const set = useCallback(
    (patch: Partial<Record<K, string | string[] | undefined | null>>) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(patch) as [K, string | string[] | undefined | null][]) {
            next.delete(k);
            if (Array.isArray(v)) v.forEach((x) => next.append(k, x));
            else if (v !== undefined && v !== null && v !== '') next.set(k, v);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const clear = useCallback(() => setParams(new URLSearchParams(), { replace: true }), [setParams]);
  const activeCount = keys.filter((k) => values[k] !== undefined && k !== ('search' as K)).length;
  return { values, set, clear, activeCount };
}

export function useDocumentTitle(title: string | undefined) {
  useEffect(() => {
    if (!title) return;
    const prev = document.title;
    document.title = `${title} · Onboarding`;
    return () => {
      document.title = prev;
    };
  }, [title]);
}
